import { useEffect, useState } from 'react';
import {
    Box,
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    FormControl,
    FormErrorMessage,
    InputGroup,
    InputRightElement,
    Link,
    Stack,
    Text,
} from '@chakra-ui/react';
import { CalendarIcon } from '@chakra-ui/icons';
import FormBase from '@/components/forms/new/FormBase';
import Input from 'components/v4/Input';
import FormLabel from 'components/v4/FormLabel';
import Button from 'components/v4/Button';
import { SingleDatepicker } from 'chakra-dayzed-datepicker';
import createApprovalCode from 'API/ApprovalCode/createApprovalCode';
import { useSelector } from 'react-redux';
import { Select } from 'chakra-react-select';
import getPartnerPlans from 'API/Partner/getPartnerPlans';
import LoadingIndicator from 'components/v4/LoadingIndicator';
import Alert from 'components/v4/Alert';
import AlertDescription from 'components/v4/AlertDescription';
import AlertIcon from 'components/v4/AlertIcon';
import {
    ChakraDatePickerDarkModeStyles,
    ChakraDatePickerStyles,
} from '@/chakratheme';
import walkthroughIds from './walkthroughIds';
import {
    chakraDatepicker,
    chakraDatepickerDarkMode,
    chakraSelectDarkMode,
    defaultChakraSelectStyle,
} from '@/constants';
import {
    validationSchema,
    selectiveSyncOptions,
    autoApprovalOptions,
} from './consts';
import ApprovalCodeName from './formFields/ApprovalCodeName';
import ApprovalCodePlan from './formFields/ApprovalCodePlan';
import ApprovalCodeCode from './formFields/ApprovalCodeCode';
import ApprovalCodeAutoApprove from './formFields/ApprovalCodeAutoApprove';
import ApprovalCodeTrialDays from './formFields/ApprovalCodeTrialDays';
import ApprovalCodeValidTo from './formFields/ApprovalCodeValiddTo';
import ApprovalCodeSelectiveSyncPolicy from './formFields/ApprovalCodeSelectiveSyncPolicy';
import { breadCrumbStyling } from 'components/forms/new/formStyles';

const initialValues = {
    code: '',
    name: '',
    trialDays: 14,
    autoApprove: 1,
    // Set default validTo to 14 days from now in UTC ISO format
    validTo: new Date(Date.UTC(
        new Date().getUTCFullYear(),
        new Date().getUTCMonth(),
        new Date().getUTCDate() + 14,
        new Date().getUTCHours(),
        new Date().getUTCMinutes(),
        new Date().getUTCSeconds()
    )).toISOString(),
    selectiveSyncPolicy: 0,
    planID: '',
};

const CRS = (props) => <Select selectedOptionColor="brand" {...props} />;

export default function ApprovalCodeCreate({
    setApprovalCodesAction,
    setSuccessAction,
}) {
    const [error, setError] = useState(null);
    const [partnerPlans, setPartnerPlans] = useState(null);
    const [validToTime, setValidToTime] = useState(() => {
        const date = new Date();
        date.setDate(date.getDate() + 14);
        return date;
    });
    const [isLoading, setIsLoading] = useState(false);

    const currentPartner = useSelector(
        (state) => state.navigationLists?.currentPartner,
    );
    const { darkMode } = useSelector((state) => state.settings);

    const chakraSelectDark = chakraSelectDarkMode(darkMode);
    const chakraStyles = {
        ...defaultChakraSelectStyle,
        ...chakraSelectDark,
    };

    const { createMode } = walkthroughIds;

    function updateFields(setFieldValue, key, value) {
        setFieldValue(key, value);
    }

    async function _getPartnerPlans() {
        getPartnerPlans(currentPartner)
            .then((response) => {
                const data = response.data.map((plan) => ({
                    label: plan.name,
                    value: plan.id,
                }));
                setPartnerPlans(data);
            })
            .catch((error) => {
                console.error(error.resposne);
            });
    }

    function setInitial90DayDate(setFieldValue) {
        var nowPlus90Days = new Date();
        nowPlus90Days.setDate(nowPlus90Days.getDate() + 90);
        setFieldValue('validTo', nowPlus90Days.toISOString());
        setValidToTime(nowPlus90Days);
    }

    useEffect(() => {
        _getPartnerPlans();
    }, []);

    if (!partnerPlans) return <LoadingIndicator />;

    const breadCrumbTrail = [
        { label: 'Approval Codes', path: '/approval-codes' },
        { label: 'Create', path: '' },
    ];

    const finalFormSubmit = async (fields, actions) => {
        console.log(validToTime)
        setIsLoading(true);
        setError(null);
        setSuccessAction(null);
        console.log('Submitting Approval Code Create Form', fields);
        // Validation
        // if (!validatefn()) {
        //     setIsLoading(false);
        //     return;
        // }

        // Hidden fields
        const hiddenFields = {
            partnerID: currentPartner,
        };

        createApprovalCode({ ...fields, ...hiddenFields })
            .then((response) => {
                if (response.status === 200) {
                    setSuccessAction('Create');
                    setApprovalCodesAction('Get');
                    setTimeout(() => {
                        setSuccessAction(null);
                    }, 6000);
                } else {
                    setError(response.response.data.message);
                }
            })
            .catch((error) => {
                if (error?.resposne?.data?.message) {
                    setError(error.resposne.data.message);
                } else {
                    setError(
                        'Something went wrong when creating an approval code.',
                    );
                }
            })
            .finally(() => {
                setIsLoading(false);
                actions.setSubmitting(false);
            });
    };

    // const formFields = (errors, touched, values, setFieldValue) => [
    //     // Name
    //     <FormControl isInvalid={!!errors?.name && touched?.name} key="name">
    //         <FormLabel>Name</FormLabel>
    //         <Input
    //             value={values?.name}
    //             onChange={(e) => updateFields(setFieldValue, 'name', e.target.value)}
    //             data-walkthroughid={createMode.ufNameInput}
    //         />
    //         <FormErrorMessage>
    //             {errors?.name}
    //         </FormErrorMessage>
    //     </FormControl>,

    //     // Plan
    //     <FormControl isInvalid={!!errors?.planID && touched?.planID} key="planID">
    //         <FormLabel>Customer Default Plan</FormLabel>
    //         <CRS
    //             value={partnerPlans.find((plan) => plan.value === values?.planID)}
    //             onChange={(e) => updateFields(setFieldValue, 'planID', e.value)}
    //             placeholder="- Select Partner Plan -"
    //             options={partnerPlans}
    //             data-walkthroughid={createMode.ufDefaultPlanSelect}
    //             chakraStyles={chakraStyles}
    //         />
    //         <FormErrorMessage>
    //             {errors?.planID}
    //         </FormErrorMessage>
    //     </FormControl>,

    //     // Selective Sync Policy
    //     <FormControl isInvalid={!!errors?.selectiveSyncPolicy && touched?.selectiveSyncPolicy} key="selectiveSyncPolicy">
    //         <FormLabel>Selective Sync Policy</FormLabel>
    //         <CRS
    //             value={selectiveSyncOptions.find((op) => op.value === values?.selectiveSyncPolicy)}
    //             onChange={(e) => updateFields(setFieldValue, 'selectiveSyncPolicy', e.value)}
    //             options={selectiveSyncOptions}
    //             data-walkthroughid={createMode.ufSelectiveSyncSelect}
    //             chakraStyles={chakraStyles}
    //         />
    //         <FormErrorMessage>
    //             {errors?.selectiveSyncPolicy}
    //         </FormErrorMessage>
    //     </FormControl>,

    //     // Code
    //     <FormControl isInvalid={!!errors?.code && touched?.code} key="code">
    //         <FormLabel>Code</FormLabel>
    //         <Input
    //             value={values?.code}
    //             onChange={(e) => updateFields(setFieldValue, 'code', e.target.value)}
    //             data-walkthroughid={createMode.ufCodeInput}
    //         />
    //         <FormErrorMessage>
    //             {errors?.code}
    //         </FormErrorMessage>
    //     </FormControl>,

    //     // Auto Approve
    //     <FormControl isInvalid={!!errors?.autoApprove && touched?.autoApprove} key="autoApprove">
    //         <FormLabel>Auto Approval</FormLabel>
    //         <CRS
    //             value={autoApprovalOptions.find((op) => op.value === values?.autoApprove)}
    //             onChange={(e) => updateFields(setFieldValue, 'autoApprove', e.value)}
    //             options={autoApprovalOptions}
    //             data-walkthroughid={createMode.ufAutoApproveSelect}
    //             chakraStyles={chakraStyles}
    //         />
    //         <FormErrorMessage>
    //             {errors?.autoApprove}
    //         </FormErrorMessage>
    //     </FormControl>,

    //     // Trial Days
    //     <FormControl isInvalid={!!errors?.trialDays && touched?.trialDays} key="trialDays">
    //         <FormLabel>Trial Duration (days)</FormLabel>
    //         <Input
    //             type="number"
    //             value={values?.trialDays}
    //             onChange={(e) => updateFields(setFieldValue, 'trialDays', e.target.value)}
    //             data-walkthroughid={createMode.ufTrialDurationInput}
    //         />
    //         <FormErrorMessage>
    //             {errors?.trialDays}
    //         </FormErrorMessage>
    //     </FormControl>,

    //     // Valid To
    //     <FormControl isInvalid={!!errors?.validTo && touched?.validTo} key="validTo">
    //         <FormLabel>Approval Code Expiry Date</FormLabel>
    //         <InputGroup
    //             data-walkthroughid={createMode.ufApprovalExpiryDate}
    //             sx={darkMode ? chakraDatepickerDarkMode : chakraDatepicker}
    //         >
    //             <SingleDatepicker
    //                 configs={{
    //                     dateFormat: 'dd-MM-yyyy',
    //                 }}
    //                 date={values?.validTo ? new Date(values?.validTo) : validToTime}
    //                 onDateChange={(value) => {
    //                     updateFields(setFieldValue, 'validTo', new Date(value).toISOString());
    //                     setValidToTime(value);
    //                 }}
    //                 propsConfigs={darkMode ? ChakraDatePickerDarkModeStyles : ChakraDatePickerStyles}
    //                 className="chakra-datepicker"
    //             />
    //             <InputRightElement
    //                 color={darkMode ? 'white' : 'gray.500'}
    //                 pointerEvents="none"
    //                 children={<CalendarIcon cursor="pointer" />}
    //             />
    //         </InputGroup>
    //         <FormErrorMessage>
    //             {errors?.validTo}
    //         </FormErrorMessage>
    //     </FormControl>,

    //     // Error
    //     error && (
    //         <Alert status="error" sx={{ margin: '25px 0' }} key="error">
    //             <AlertIcon />
    //             <AlertDescription>{error}</AlertDescription>
    //         </Alert>
    //     ),
    // ];

    return (
        <>
            <Breadcrumb sx={breadCrumbStyling}>
                {breadCrumbTrail?.map((crumb, index) => (
                    <BreadcrumbItem key={index}>
                        {Array.isArray(crumb.path) ? (
                            <BreadcrumbLink
                                as={Link}
                                to={crumb.path[0]}
                                state={crumb.path[1]}
                                onClick={index === 0 ? () => setApprovalCodesAction('Get') : undefined}
                            >
                                {crumb.label}
                            </BreadcrumbLink>
                        ) : (
                            <BreadcrumbLink
                                as={Link}
                                to={crumb.path}
                                onClick={index === 0 ? () => setApprovalCodesAction('Get') : undefined}
                            >
                                {crumb.label}
                            </BreadcrumbLink>
                        )}
                    </BreadcrumbItem>
                ))}
            </Breadcrumb>
            <Text
                sx={{
                    fontSize: '20',
                    fontWeight: '500',
                    // marginTop: '15px',
                    // marginBottom: '15px',
                }}>
                Create Approval Code
            </Text>
            <FormBase
                initialValues={initialValues}
                finalFormSubmit={finalFormSubmit}
                validationSchema={validationSchema}
                ufBackBtn={createMode.ufBackBtn}
                ufSaveBtn={createMode.ufCreateBtn}
                customClick={true}
                onBack={() => setApprovalCodesAction('Get')}
            >
                {({ errors = {}, touched = {} }) => [
                        <ApprovalCodeName 
                            key="name" 
                            errors={errors} 
                            touched={touched} 
                            darkMode={darkMode} 
                        />,
                        <ApprovalCodePlan 
                            key="plan" 
                            errors={errors} 
                            touched={touched} 
                            darkMode={darkMode} 
                            partnerPlans={partnerPlans} 
                        />,
                        <ApprovalCodeSelectiveSyncPolicy 
                            key="selectiveSync" 
                            errors={errors} 
                            touched={touched} 
                            darkMode={darkMode} 
                        />,
                        <ApprovalCodeCode 
                            key="code" 
                            errors={errors} 
                            touched={touched} 
                            darkMode={darkMode} 
                        />,
                        <ApprovalCodeAutoApprove 
                            key="autoApprove" 
                            errors={errors} 
                            touched={touched} 
                            darkMode={darkMode} 
                        />,
                        <ApprovalCodeTrialDays 
                            key="trialDays" 
                            errors={errors} 
                            touched={touched} 
                            darkMode={darkMode} 
                        />,
                        <ApprovalCodeValidTo 
                            key="validTo" 
                            errors={errors} 
                            touched={touched} 
                            darkMode={darkMode} 
                            validToTime={validToTime} 
                            setValidToTime={setValidToTime} 
                        />,
                        error && (
                            <Alert key="error" status="error" sx={{ margin: '25px 0' }}>
                                <AlertIcon />
                                <AlertDescription>{error}</AlertDescription>
                            </Alert>
                        )
                ].filter(Boolean)}
            </FormBase>
        </>
    );
}